import React, { useEffect, useMemo, useState } from "react";
import { MultiSelect, Select } from "@mantine/core";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { readAddressTypes } from "../helpers/addressTypes";

const toSelectData = (items = []) =>
  items.map((item) => ({
    value: item.value,
    label: item.label,
  }));

const extraValues = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return [];
};

const mergeTypeOptions = (items, value) => {
  const data = toSelectData(items);
  extraValues(value).forEach((item) => {
    if (!data.some((option) => option.value === item)) {
      data.unshift({ value: item, label: item });
    }
  });
  return data;
};

const selectedAddressTypes = (values, name) => {
  const fromTypes = readAddressTypes({
    type: _.get(values, name),
    types: _.get(values, "types"),
  });
  return fromTypes;
};

const createTypeValue = (query) => query.trim().toLowerCase().replace(/\s+/g, "");

const CreatableTypeSelect = ({
  form,
  name,
  label,
  items = [],
  required = false,
  disabled = false,
  placeholder,
  multiple = false,
  ...props
}) => {
  const { t } = useTranslation();
  const currentValue = multiple
    ? selectedAddressTypes(form.values, name)
    : (_.get(form.values, name) ?? null);
  const [extraOptions, setExtraOptions] = useState([]);
  const savedTypes = _.get(form.values, "types");
  const legacyType = _.get(form.values, name);
  const savedTypeKey = Array.isArray(savedTypes) ? savedTypes.join("\0") : "";
  const legacyTypeKey = Array.isArray(legacyType)
    ? legacyType.join("\0")
    : String(legacyType ?? "");

  useEffect(() => {
    if (!multiple) return;
    if (Array.isArray(savedTypes) && savedTypes.length) return;
    const selected = selectedAddressTypes(form.values, name);
    if (!selected.length) return;
    form.setFieldValue("types", selected);
    if (legacyType !== selected[0]) {
      form.setFieldValue(name, selected[0]);
    }
  }, [multiple, form, name, savedTypeKey, legacyTypeKey, savedTypes, legacyType]);

  const data = useMemo(() => {
    const base = mergeTypeOptions(items, currentValue);
    const merged = [...base];
    extraOptions.forEach((option) => {
      if (!merged.some((item) => item.value === option.value)) {
        merged.push(option);
      }
    });
    return merged.map((item) => ({
      value: item.value,
      label: t(item.label),
    }));
  }, [items, currentValue, extraOptions, t]);

  const inputProps = form.getInputProps(name);
  const { onChange: parentOnChange, value: _parentValue, ...restProps } = props;
  const createLabel = (query) => t("Add type: {{name}}", { name: query.trim() });
  const onCreate = (query) => {
    const value = createTypeValue(query);
    if (!value) return null;
    const option = { value, label: query.trim() };
    setExtraOptions((current) => [...current, option]);
    return value;
  };

  if (multiple) {
    const selected = Array.isArray(currentValue) ? currentValue : [];
    return (
      <MultiSelect
        {...restProps}
        label={label ? t(label) : undefined}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        searchable
        creatable
        data={data}
        value={selected}
        onChange={(next) => {
          const list = Array.isArray(next) ? next.filter(Boolean) : [];
          if (!list.length) return;
          form.setFieldValue("types", list);
          form.setFieldValue(name, list[0]);
          if (typeof parentOnChange === "function") parentOnChange(list[0]);
        }}
        getCreateLabel={createLabel}
        onCreate={onCreate}
        withinPortal
        zIndex={1000}
        error={inputProps.error}
      />
    );
  }

  return (
    <Select
      label={label ? t(label) : undefined}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      searchable
      clearable
      creatable
      data={data}
      getCreateLabel={createLabel}
      onCreate={onCreate}
      withinPortal
      zIndex={1000}
      {...inputProps}
      {...props}
    />
  );
};

export default CreatableTypeSelect;
